import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
} from 'react'
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import { closeHistory } from '@tiptap/pm/history'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Underline from '@tiptap/extension-underline'
import tippy from 'tippy.js'
import EmojiPicker from 'emoji-picker-react'
import { LinkDialog } from './LinkDialog'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { ReactComponent as FileUploadIcon } from 'assets/svgs/file-upload-icon.svg'
import { ReactComponent as MentionIcon } from 'assets/svgs/mention-icon.svg'
import { ReactComponent as HyperlinkIcon } from 'assets/svgs/hyperlink-icon.svg'
import { ReactComponent as BoldIcon } from 'assets/svgs/bold-icon.svg'
import { ReactComponent as ItalicIcon } from 'assets/svgs/italic-icon.svg'
import { ReactComponent as StrikethroughIcon } from 'assets/svgs/strikethrought-icon.svg'
import { ReactComponent as ListBulletIcon } from 'assets/svgs/list-bullet-icon.svg'
import { ReactComponent as TrashIcon } from 'assets/svgs/trash.svg'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { XButtonBackground } from '../../assets/XButtonBackground'
import appColors from 'appColors'

// Import Tiptap CSS
// import '@tiptap/core/style.css'

// Mention list component
interface MentionListProps {
  items: Array<{ id: string; label: string; email: string; firstName: string; lastName: string }>
  command: (item: {
    id: string
    label: string
    email: string
    firstName: string
    lastName: string
  }) => void
}

interface MentionListRef {
  onKeyDown: (event: KeyboardEvent) => boolean
}

const MentionList = React.forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const { items, command } = props
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  // Highlight the first suggestion whenever the list changes (new query)
  useEffect(() => setSelectedIndex(0), [items])

  // Keep the highlighted suggestion visible while moving with the arrow keys
  useEffect(() => {
    const el = listRef.current?.children[selectedIndex] as HTMLElement | undefined
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedIndex])

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index]
      if (item) {
        command(item)
      }
    },
    [items, command]
  )

  // Handle click with proper event handling to prevent scroll issues
  const handleItemClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.preventDefault()
      e.stopPropagation()
      selectItem(index)
    },
    [selectItem]
  )

  // Expose onKeyDown to TipTap via ref
  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event: KeyboardEvent) => {
        // "No result": let the key behave normally (Enter sends, Tab moves focus)
        if (!items.length) return false
        if (event.key === 'ArrowDown') {
          setSelectedIndex((index) => (index + 1) % items.length)
          return true
        }
        if (event.key === 'ArrowUp') {
          setSelectedIndex((index) => (index + items.length - 1) % items.length)
          return true
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          selectItem(selectedIndex)
          return true
        }
        return false
      },
    }),
    [items.length, selectItem, selectedIndex]
  )

  return (
    <div
      ref={listRef}
      className="bg-white border border-gray-200 rounded-lg shadow-lg min-w-[320px] max-h-[280px] overflow-y-auto"
      onMouseDown={(e) => e.preventDefault()} // Prevent focus loss from editor
    >
      {items.length ? (
        items.map((item, index) => {
          const isCoachLogic = item.id === 'coachLogic'
          const initial = item.label
            ?.split(' ')
            .map((name: string) => name[0])
            .join('')

          return (
            <div
              key={item.id}
              role="button"
              tabIndex={-1}
              className={`w-full px-4 py-3 cursor-pointer transition-colors duration-150 text-left ${
                index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'
              }`}
              onMouseDown={(e) => handleItemClick(e, index)}
            >
              <div className="flex items-center gap-3">
                {isCoachLogic ? (
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-lg">
                    🤖
                  </div>
                ) : (
                  <AppAvatar size="sm" type={'mixed'} shape="Rounded" text={initial} />
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{item.label}</div>
                  {isCoachLogic && (
                    <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                      AI Assistant
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500">No result</div>
      )}
    </div>
  )
})

MentionList.displayName = 'MentionList'

export interface MentionItem {
  id: string
  label: string
  email?: string
  firstName?: string
  lastName?: string
}

export interface UploadedAttachment {
  type: 'image' | 'file'
  url: string
  fileName: string
  mimeType: string
  fileSize: number
}

export interface SubmitPayload {
  text: string
  html: string
  mentions: MentionItem[]
  attachments: UploadedAttachment[]
  hasMentions: boolean
}

type UploadProgressHandler = (progress: number) => void

interface RichTextInputProps {
  placeholder?: string
  onSubmit?: (payload: SubmitPayload) => void | Promise<void>
  onTextChange?: (text: string) => void
  initialContent?: string
  value?: string
  onChange?: (html: string) => void
  mode?: 'message' | 'form'
  disabled?: boolean
  rightActions?: React.ReactNode
  onAttachClick?: () => void
  mentionProvider?: (query: string) => Promise<MentionItem[]> | MentionItem[]
  initialMentions?: MentionItem[]
  uploadAdapter?: (file: File, onProgress?: UploadProgressHandler) => Promise<UploadedAttachment>
  /** Finished uploads held by the parent (files, images, voice) — sendable without text. */
  hasAttachments?: boolean
  groupFormatting?: boolean // New prop to enable grouped formatting toolbar
  /** Called when user presses Ctrl+K / Cmd+K (quick search). */
  onQuickSearch?: () => void
  /** When input is empty and user presses Up, this content is loaded for editing. */
  lastSentMessage?: string
  /** Called with the editor HTML after every user edit (e.g. to keep an unsent draft). */
  onHtmlChange?: (html: string) => void
  /** Replaces the default Esc behaviour (clear the draft), e.g. to cancel editing a message. */
  onEscape?: () => void
}
export const RichTextInput: React.FC<RichTextInputProps> = ({
  placeholder = 'Type a message...',
  onSubmit,
  onTextChange,
  initialContent = '',
  value,
  onChange,
  mode = 'message',
  disabled = false,
  rightActions,
  onAttachClick,
  mentionProvider,
  initialMentions = [],
  uploadAdapter,
  hasAttachments = false,
  groupFormatting = false,
  onQuickSearch,
  lastSentMessage,
  onHtmlChange,
  onEscape,
}) => {
  const isControlled = value !== undefined
  const contentToUse = isControlled ? value : initialContent
  // Helper to check if content is empty (handles HTML strings)
  const isContentEmpty = (content: string | undefined): boolean => {
    if (!content) return true
    // Strip HTML tags and check if there's any text
    const textOnly = content.replace(/<[^>]*>/g, '').trim()
    return textOnly.length === 0
  }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [showFormattingMenu, setShowFormattingMenu] = useState(false)
  // Initialize isEditorEmpty based on controlled value or initialContent
  const [isEditorEmpty, setIsEditorEmpty] = useState(() =>
    isContentEmpty(isControlled ? value : initialContent)
  )
  const [linkDialogData, setLinkDialogData] = useState<{
    text: string
    url: string
    isEditing: boolean
  }>({ text: '', url: '', isEditing: false })
  const emojiButtonRef = useRef<HTMLButtonElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const formattingButtonRef = useRef<HTMLButtonElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // In-flight lock: repeated Enter / Send clicks while a send is pending must not send again
  const isSubmittingRef = useRef(false)
  // Read at submit time so a stale keydown handler still sees attachments already sent/cleared
  const hasAttachmentsRef = useRef(hasAttachments)
  hasAttachmentsRef.current = hasAttachments
  // The editor's onUpdate is bound once, so read the latest callback through a ref
  const onHtmlChangeRef = useRef(onHtmlChange)
  onHtmlChangeRef.current = onHtmlChange
  // @mention popup: whether it lists anyone, and the last key it consumed. Keys meant for the popup
  // (Enter/Tab pick, arrows move, Esc closes) must not also send the message or clear the draft.
  const mentionHasItemsRef = useRef(false)
  const mentionKeyEventRef = useRef<KeyboardEvent | null>(null)
  const pendingUploadsRef = useRef<
    Array<{
      id: string
      file: File
      status: 'queued' | 'uploading' | 'uploaded' | 'failed'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }>
  >([])
  const [pendingUploads, setPendingUploads] = useState<
    Array<{
      id: string
      file: File
      status: 'queued' | 'uploading' | 'uploaded' | 'failed'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }>
  >([])
  // Close emoji picker on click outside
  useEffect(() => {
    if (!showEmojiPicker) return
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  useEffect(() => {
    pendingUploadsRef.current = pendingUploads
  }, [pendingUploads])
  useEffect(() => {
    return () => {
      pendingUploadsRef.current.forEach((upload) =>
        upload.previewUrl ? URL.revokeObjectURL(upload.previewUrl) : null
      )
    }
  }, [])

  const clearPendingUploads = useCallback(() => {
    setPendingUploads((prev) => {
      prev.forEach((upload) => upload.previewUrl && URL.revokeObjectURL(upload.previewUrl))
      return []
    })
  }, [])

  const removePendingUpload = useCallback((id: string) => {
    setPendingUploads((prev) => {
      const target = prev.find((upload) => upload.id === id)
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter((upload) => upload.id !== id)
    })
  }, [])

  // Create mention suggestions from team members
  // Build mention suggestions if/when mention feature is re-enabled
  // This will be populated when integrated with Stream Chat or when mentionProvider is used
  const mentionSuggestions: MentionItem[] = useMemo(() => [], [])

  // Create custom keymap extension: new line (Shift+Enter / Mod+Enter), Enter to submit in message mode
  const customKeymapExtension = useMemo(
    () =>
      Extension.create({
        name: 'customListKeymap',
        priority: 1000,
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => {
              if (this.editor.isActive('bulletList') || this.editor.isActive('orderedList')) {
                return this.editor.chain().focus().splitListItem('listItem').run()
              }
              return this.editor.chain().focus().setHardBreak().run()
            },
            'Mod-Enter': () => this.editor.chain().focus().setHardBreak().run(),
            // In message mode, swallow Enter (submit handled in keydown); form mode allows default newline
            Enter: () => {
              // Open @mention popup with results: let it pick the highlighted person instead
              if (mentionHasItemsRef.current) return false
              if (mode === 'message') return true
              return false
            },
          }
        },
      }),
    [mode]
  )

  const editor = useEditor({
    extensions: [
      // Custom keymap: Shift+Enter in lists creates a new list item (no hard break)
      customKeymapExtension,
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        hardBreak: {},
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'text-[#5B93FF] underline hover:text-[#4A7FE6] cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }).extend({
        inclusive: false,
      }),
      Underline,
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-app-primary-10 rounded px-1 py-0.5 text-app-primary-70 font-medium',
        },
        renderText({ node }) {
          return `@${node.attrs.label || node.attrs.id}`
        },
        renderHTML({ node }) {
          return [
            'mention',
            {
              id: node.attrs.id,
            },
            `@${node.attrs.label}`,
          ]
        },
        suggestion: {
          char: '@',
          allowSpaces: false,
          allowedPrefixes: null, // Allow @ after any character (not just specific prefixes)
          items: ({ query }) => {
            const q = (query || '').toLowerCase()
            if (mentionProvider) {
              return mentionProvider(q) as any
            }
            return mentionSuggestions.filter((item) => item.label.toLowerCase().includes(q))
          },
          render: () => {
            let component: ReactRenderer | null = null
            let popup: any
            return {
              onStart: (props) => {
                mentionHasItemsRef.current = props.items.length > 0
                component = new ReactRenderer(MentionList as any, {
                  props: {
                    ...props,
                    command: (item: any) => {
                      props.command({
                        id: item.id,
                        label: item.label,
                      } as any)
                    },
                  },
                  editor: props.editor,
                })
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as any,
                  appendTo: () => document.body,
                  content: (component as any).element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  offset: [0, 8],
                  theme: 'light',
                  zIndex: 99999,
                })
              },
              onUpdate(props) {
                mentionHasItemsRef.current = props.items.length > 0
                component?.updateProps({
                  ...props,
                  command: (item: any) => {
                    props.command({
                      id: item.id,
                      label: item.label,
                    } as any)
                  },
                })
                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect,
                })
              },
              onKeyDown(props) {
                let handled: boolean
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide()
                  handled = true
                } else {
                  handled = (component as any)?.ref?.onKeyDown?.(props.event) ?? false
                }
                if (handled) mentionKeyEventRef.current = props.event
                return handled
              },
              onExit() {
                mentionHasItemsRef.current = false
                if (popup && popup[0] && !popup[0].state.isDestroyed) {
                  popup[0].destroy()
                }
                if (component) {
                  component.destroy()
                }
              },
            }
          },
        },
      }).extend({
        // Add parseHTML to recognize <mention> tags when loading content
        parseHTML() {
          return [
            {
              tag: 'mention',
              getAttrs: (node) => {
                if (typeof node === 'string') return false
                const id = node.getAttribute('id')
                // Extract label from text content (removes @ prefix if present)
                const textContent = node.textContent || ''
                const label = textContent.startsWith('@') ? textContent.slice(1) : textContent
                return { id, label }
              },
            },
          ]
        },
      }),
    ],
    content: contentToUse || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      const html = editor.getHTML()
      onTextChange?.(text)
      onHtmlChangeRef.current?.(html)
      // In controlled mode, call onChange with HTML
      if (isControlled && onChange) {
        onChange(html)
      }
      // Check if editor is truly empty - also consider active formatting like bullet lists
      const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
      setIsEditorEmpty(editor.isEmpty && !hasActiveFormatting)
    },
    onSelectionUpdate: ({ editor }) => {
      // Auto-deactivate link formatting when cursor moves away from links
      if (!editor.isActive('link')) {
        // This helps prevent new text from being included in links
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[70px] px-3 py-2',
      } as any,
    },
  })

  // Update editor placeholder attribute when placeholder prop changes
  useEffect(() => {
    if (!editor) return
    const editorElement = editor.view.dom
    if (editorElement) {
      editorElement.setAttribute('data-placeholder', placeholder)
    }
  }, [editor, placeholder])

  // Sync isEditorEmpty with editor's actual state when editor is created or content changes
  useEffect(() => {
    if (!editor) return
    // Check editor's isEmpty state to ensure placeholder visibility is correct
    // Also consider active formatting like bullet lists
    const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
    const isEmpty = editor.isEmpty && !hasActiveFormatting
    setIsEditorEmpty(isEmpty)
  }, [editor])
  // Watch for content changes to keep isEditorEmpty in sync
  useEffect(() => {
    if (!editor) return

    // Create a timer to check if editor is empty after a short delay
    // This ensures we catch state changes after programmatic content clearing
    const checkEmpty = () => {
      const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
      const isEmpty = editor.isEmpty && !hasActiveFormatting
      setIsEditorEmpty(isEmpty)
    }

    // Check immediately and after a short delay to catch async updates
    checkEmpty()
    const timer = setTimeout(checkEmpty, 50)

    return () => clearTimeout(timer)
  }, [editor?.state.doc, editor])

  // Handle emoji insertion
  const handleEmojiClick = useCallback(
    (emojiData: any) => {
      if (editor) {
        editor.chain().focus().insertContent(emojiData.emoji).run()
        setShowEmojiPicker(false)
      }
    },
    [editor]
  )

  // Handle link dialog
  const handleLinkClick = useCallback(() => {
    if (!editor) return

    // Check if there's already a link at the current position
    if (editor.isActive('link')) {
      // If there's a link, select the entire link first
      editor.chain().focus().extendMarkRange('link').run()
    }

    // Get selected text (after potential extension)
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to
    )

    const { from, to } = editor.state.selection
    const linkMark = editor.state.doc.rangeHasMark(from, to, editor.schema.marks.link)

    if (linkMark) {
      // If there's a link, get its attributes for editing
      const linkAttrs = editor.getAttributes('link')
      setLinkDialogData({
        text: selectedText || linkAttrs.href || '',
        url: linkAttrs.href || '',
        isEditing: true,
      })
    } else {
      setLinkDialogData({
        text: selectedText,
        url: '',
        isEditing: false,
      })
    }

    setShowLinkDialog(true)
  }, [editor])

  const handleLinkSave = useCallback(
    (text: string, url: string) => {
      if (!editor || !url) return

      const linkText = text || url

      // Helper to insert link and prevent bleeding (typing into the link)
      // We insert the link as HTML to ensure proper rendering
      // Then we explicitly clear the 'link' mark from storedMarks so the next typed char is clean
      const chain = editor.chain().focus()

      if (editor.state.selection.from !== editor.state.selection.to || editor.isActive('link')) {
        // Editing: select full link first
        chain.extendMarkRange('link')
      }

      chain
        .insertContent(`<a href="${url}">${linkText}</a>`)
        .command(({ tr, state }) => {
          const linkType = state.schema.marks.link
          // Get current marks at cursor (which is now at end of inserted link)
          // We default to the marks present at the cursor position
          const currentMarks = tr.storedMarks || state.storedMarks || state.selection.$from.marks()

          // Remove link mark from the set of marks for the next input
          const newMarks = currentMarks.filter((m) => m.type !== linkType)
          tr.setStoredMarks(newMarks)

          return true
        })
        .run()
    },
    [editor]
  )

  const handleLinkRemove = useCallback(() => {
    if (!editor) return
    editor.chain().focus().unsetLink().run()
  }, [editor])

  // File handling: managed locally in rich text input
  const triggerFilePicker = useCallback(() => {
    // Only trigger the file input directly - onAttachClick is handled separately in parent component
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const items: Array<{
      id: string
      file: File
      status: 'queued'
      url?: string
      isImage: boolean
      progress: number
      startedAt?: number
      etaSeconds?: number
      previewUrl?: string
    }> = []

    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} exceeds the 5MB limit.`)
        return
      }

      const isImage = file.type?.startsWith('image/')
      items.push({
        id: `${Date.now()}-${file.name}-${Math.random()}`,
        file,
        status: 'queued',
        isImage,
        progress: 0,
        startedAt: undefined,
        etaSeconds: undefined,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      })
    })
    setPendingUploads((prev) => [...prev, ...items])
    e.target.value = ''
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!editor || isSubmittingRef.current) return

      const text = editor.getText()
      const html = editor.getHTML()

      // getHTML() already writes mentions as <mention id="…">@Name</mention> (see Mention renderHTML)
      // and keeps every other mark and line break, so messages with mentions use it as well
      let hasMentions = false
      editor.state.doc.descendants((node: any) => {
        if (node.type?.name === 'mention') hasMentions = true
      })

      // Check for uploads (local only)
      const hasUploads = pendingUploads.length > 0

      // If nothing to send and no uploads queued, bail
      if (!text.trim() && !hasUploads && !hasAttachmentsRef.current) return

      isSubmittingRef.current = true
      try {
        // Upload pending files via provided adapter and build attachments
        const attachments: UploadedAttachment[] = []
        if (pendingUploads.length > 0 && uploadAdapter) {
          for (const item of pendingUploads) {
            setPendingUploads((prev) =>
              prev.map((u) =>
                u.id === item.id
                  ? {
                      ...u,
                      status: 'uploading',
                      progress: 0,
                      etaSeconds: undefined,
                      startedAt: Date.now(),
                    }
                  : u
              )
            )
            try {
              const uploaded = await uploadAdapter(item.file, (progress) => {
                setPendingUploads((prev) =>
                  prev.map((u) =>
                    u.id === item.id
                      ? {
                          ...u,
                          progress,
                          etaSeconds:
                            progress > 0 && u.startedAt
                              ? Math.max(
                                  0,
                                  ((100 - progress) / progress) *
                                    ((Date.now() - u.startedAt) / 1000)
                                )
                              : undefined,
                        }
                      : u
                  )
                )
              })
              attachments.push(uploaded)
              setPendingUploads((prev) =>
                prev.map((u) =>
                  u.id === item.id
                    ? { ...u, status: 'uploaded', url: uploaded.url, progress: 100, etaSeconds: 0 }
                    : u
                )
              )
            } catch (err) {
              console.error('Upload failed for file:', item.file.name, err)
              setPendingUploads((prev) =>
                prev.map((u) => (u.id === item.id ? { ...u, status: 'failed' } : u))
              )
            }
          }
        }

        // Collect mentioned users from document in the same format as MentionTextArea
        const mentionedUserIds: string[] = []
        const mentions: any[] = []
        editor.state.doc.descendants((node: any) => {
          if (node.type?.name === 'mention' && node.attrs?.id) {
            const mentionData = mentionSuggestions.find((s) => s.id === node.attrs.id)
            mentionedUserIds.push(String(node.attrs.id))
            mentions.push({
              id: node.attrs.id,
              display: node.attrs.label,
              email: mentionData?.email || '',
              firstName: mentionData?.firstName || '',
              lastName: mentionData?.lastName || '',
            })
          }
        })

        // Emit payload to parent
        await onSubmit?.({
          text: hasMentions ? html : text,
          html,
          attachments,
          mentions,
          hasMentions,
        })
        editor.commands.clearContent()
        clearPendingUploads()
        // Ensure isEditorEmpty is set to true after clearing
        setIsEditorEmpty(true)
      } catch (err) {
        editor.commands.clearContent()
        // Ensure isEditorEmpty is set to true after clearing
        setIsEditorEmpty(true)
      } finally {
        isSubmittingRef.current = false
      }
    },
    [editor, pendingUploads, uploadAdapter, onSubmit, clearPendingUploads, mentionSuggestions]
  )

  // Sync editor content when value prop changes (controlled mode)
  useEffect(() => {
    if (!editor || !isControlled) return
    const currentHtml = editor.getHTML()
    if (currentHtml !== value) {
      editor.commands.setContent(value || '', false)
      // Check if truly empty after setting content
      const hasActiveFormatting = editor.isActive('bulletList') || editor.isActive('orderedList')
      setIsEditorEmpty(editor.isEmpty && !hasActiveFormatting)
    }
  }, [value, editor, isControlled])

  // Handle keyboard shortcuts (only when editor or its container has focus)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!editor) return
    const el = editor.view.dom
    const container = el?.closest?.('.rich-text-input-container')
    if (container)
      (editorContainerRef as React.MutableRefObject<HTMLElement | null>).current =
        container as HTMLElement
  }, [editor])

  useEffect(() => {
    if (!editor) return

    const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform)

    const handleKeyDown = (event: KeyboardEvent) => {
      const container = editorContainerRef.current
      const isEditorFocused =
        container &&
        (document.activeElement === editor.view.dom || container.contains(document.activeElement))
      if (!isEditorFocused) return

      // Key already handled by the @mention popup (pick / move / close): don't also send or clear
      if (event === mentionKeyEventRef.current) return

      // Platform-aware modifier: ⌘ on Mac, Ctrl on Windows/Linux
      const mod = isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey

      // Esc: close the emoji picker if open; otherwise clear input (or run the caller's Esc action)
      if (event.key === 'Escape') {
        event.preventDefault()
        if (showEmojiPicker) {
          setShowEmojiPicker(false)
          editor.commands.focus()
          return
        }
        if (onEscape) {
          onEscape()
          return
        }
        // Clear as its own undo step so ⌘/Ctrl+Z always brings the draft back
        editor
          .chain()
          .command(({ tr }) => {
            closeHistory(tr)
            return true
          })
          .clearContent(true)
          .run()
        clearPendingUploads()
        setIsEditorEmpty(true)
        return
      }

      // Up Arrow when empty: load last sent message for editing
      if (event.key === 'ArrowUp' && lastSentMessage && isEditorEmpty) {
        event.preventDefault()
        editor.commands.setContent(lastSentMessage, false)
        setIsEditorEmpty(false)
        return
      }

      // Enter (no modifier): send message in message mode
      if (
        mode === 'message' &&
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault()
        event.stopPropagation()
        handleSubmit({ preventDefault: () => {} } as React.FormEvent)
        return
      }

      // Ctrl/⌘ + K: quick search
      // Note: Bold (Ctrl/⌘+B), Italic (Ctrl/⌘+I), Underline (Ctrl/⌘+U),
      // Strikethrough (Ctrl/⌘+Shift+X), Undo (Ctrl/⌘+Z), Redo (Ctrl/⌘+Shift+Z)
      // are all handled natively by Tiptap's extensions with platform-aware Mod- shortcuts.
      if (mod && event.key === 'k') {
        event.preventDefault()
        onQuickSearch?.()
        return
      }

      // Ctrl/⌘ + L: insert link (opens link dialog)
      if (mod && event.key === 'l') {
        event.preventDefault()
        handleLinkClick()
        return
      }

      // Ctrl+Y (Windows): redo — Tiptap handles ⌘+Shift+Z on Mac via History
      if (!isMac && event.ctrlKey && event.key === 'y' && !event.shiftKey) {
        event.preventDefault()
        editor.chain().focus().redo().run()
        return
      }

      // Delete last word: Ctrl+Backspace (Win) / Option+Backspace (Mac)
      if (event.key === 'Backspace' && (event.ctrlKey || event.altKey)) {
        event.preventDefault()
        const { from } = editor.state.selection
        const textBefore = editor.state.doc.textBetween(0, from)
        const trimmed = textBefore.replace(/\s+$/, '')
        const lastSpace = trimmed.lastIndexOf(' ')
        const fromPos = lastSpace === -1 ? 0 : lastSpace + 1
        editor.chain().focus().deleteRange({ from: fromPos, to: from }).run()
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    editor,
    handleSubmit,
    handleLinkClick,
    mode,
    isEditorEmpty,
    lastSentMessage,
    onQuickSearch,
    clearPendingUploads,
    showEmojiPicker,
    onEscape,
  ])

  if (!editor) {
    return null
  }

  // Helper function to format file size
  const formatFileSizeDisplay = (bytes: number): string => {
    if (bytes < 1000) return `${bytes} B`
    if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`
    if (bytes < 1_000_000_000) return `${Math.round(bytes / 1_000_000)} MB`
    return `${Math.round(bytes / 1_000_000_000)} GB`
  }

  return (
    <div className="rich-text-input-container">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Input container matching design */}
        <div className="border border-app-primary-30 rounded-xl bg-white focus-within:border-app-primary-60 transition-colors shadow">
          {/* Pending uploads inside container */}
          {pendingUploads.length > 0 && (
            <div className="px-3 pt-3 pb-2 space-y-2 border-b border-app-grey-25">
              {pendingUploads.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 p-2 bg-app-grey-5 rounded-lg border border-app-grey-25"
                >
                  <div className="flex-shrink-0">
                    {u.isImage && u.previewUrl ? (
                      <img
                        src={u.previewUrl}
                        alt={u.file.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-app-primary-60 flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-grey-80 truncate">{u.file.name}</p>
                    <p className="text-xs text-app-grey-60 mt-0.5">
                      {formatFileSizeDisplay(u.file.size)}
                    </p>

                    {/* Loading indicator */}
                    {u.status === 'uploading' && (
                      <div className="mt-1.5">
                        <div className="w-full bg-gray-200 rounded-full h-1">
                          <div
                            className="bg-app-primary-60 h-1 rounded-full transition-all duration-300"
                            style={{ width: `${u.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {u.status === 'queued' && (
                      <p className="text-xs text-app-grey-60 mt-1">Queued for upload</p>
                    )}
                    {u.status === 'failed' && (
                      <p className="text-xs text-red-500 mt-1">Upload failed</p>
                    )}
                  </div>

                  {/* Remove button with trash icon */}
                  <button
                    type="button"
                    onClick={() => removePendingUpload(u.id)}
                    className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                    aria-label="Remove file"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Editor with placeholder overlay */}
          <div className="relative">
            {editor ? <EditorContent editor={editor} /> : null}
            {isEditorEmpty && !disabled && (
              <div className="pointer-events-none absolute left-3 right-3 top-2 text-app-grey-60 text-sm">
                {placeholder}
              </div>
            )}
          </div>

          {/* Toolbar inside container bottom */}
          <div className="flex items-center justify-between px-2 py-2">
            <div className="rich-text-toolbar-buttons flex items-center gap-1">
              {groupFormatting ? (
                <>
                  {/* Toggle button - Aa or Close */}
                  <button
                    ref={formattingButtonRef}
                    type="button"
                    onClick={() => setShowFormattingMenu(!showFormattingMenu)}
                    className="rounded hover:bg-app-grey-20 transition-all duration-200"
                    title={showFormattingMenu ? 'Close formatting' : 'Text Formatting'}
                  >
                    {showFormattingMenu ? (
                      // Close icon with primary circular background
                      <XButtonBackground
                        circleFill={
                          (appColors['app-primary'] as Record<string, string>)?.['60'] ?? '#705AF8'
                        }
                      />
                    ) : (
                      // Aa icon
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <text
                          x="3"
                          y="15"
                          fontSize="14"
                          fontWeight="600"
                          fill="#3F3F46"
                          fontFamily="system-ui, -apple-system, sans-serif"
                        >
                          Aa
                        </text>
                      </svg>
                    )}
                  </button>

                  {/* Formatting buttons - animate in/out */}
                  <div
                    className="flex items-center gap-1 overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      maxWidth: showFormattingMenu ? '300px' : '0px',
                      opacity: showFormattingMenu ? 1 : 0,
                    }}
                  >
                    {/* Bold */}
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                        editor.isActive('bold') ? 'bg-app-grey-20' : ''
                      }`}
                      title="Bold (Ctrl+B)"
                    >
                      <BoldIcon />
                    </button>

                    {/* Italic */}
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                        editor.isActive('italic') ? 'bg-app-grey-20' : ''
                      }`}
                      title="Italic (Ctrl+I)"
                    >
                      <ItalicIcon />
                    </button>

                    {/* Strikethrough */}
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleStrike().run()}
                      className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                        editor.isActive('strike') ? 'bg-app-grey-20' : ''
                      }`}
                      title="Strikethrough"
                    >
                      <StrikethroughIcon />
                    </button>

                    {/* Link */}
                    <button
                      type="button"
                      onClick={handleLinkClick}
                      className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                        editor.isActive('link') ? 'bg-app-grey-20' : ''
                      }`}
                      title={editor.isActive('link') ? 'Edit Link' : 'Insert Link (Ctrl+L)'}
                    >
                      <HyperlinkIcon />
                    </button>

                    {/* Bullet List */}
                    <button
                      type="button"
                      onClick={() => {
                        if (editor.isActive('bulletList')) {
                          editor.chain().focus().liftListItem('listItem').run()
                          return
                        }

                        const { state } = editor
                        const { $from } = state.selection
                        const nodeBefore = $from.nodeBefore

                        if (nodeBefore && nodeBefore.type.name === 'hardBreak') {
                          const hardBreakPos = $from.pos - nodeBefore.nodeSize
                          editor
                            .chain()
                            .focus()
                            .setTextSelection(hardBreakPos)
                            .deleteNode('hardBreak')
                            .splitBlock()
                            .toggleBulletList()
                            .run()
                          return
                        }

                        const posInParent = $from.parentOffset
                        const parentNode = $from.parent

                        if (
                          !editor.isActive('bulletList') &&
                          posInParent > 0 &&
                          posInParent < parentNode.content.size
                        ) {
                          editor.chain().focus().splitBlock().toggleBulletList().run()
                          return
                        }

                        editor.chain().focus().toggleBulletList().run()
                      }}
                      className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                        editor.isActive('bulletList') ? 'bg-app-grey-20' : ''
                      }`}
                      title="Bullet List"
                    >
                      <ListBulletIcon />
                    </button>
                  </div>

                  {/* Attach file - hide when formatting menu is open, only show if onAttachClick is provided */}
                  {!showFormattingMenu && onAttachClick && (
                    <>
                      <button
                        type="button"
                        onClick={onAttachClick ?? triggerFilePicker}
                        className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                        title="Attach file"
                      >
                        <FileUploadIcon />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </>
                  )}

                  {/* Emoji - hide when formatting menu is open */}
                  {!showFormattingMenu && (
                    <div className="relative">
                      <button
                        ref={emojiButtonRef}
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                        title="Insert Emoji"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.6517 12.6517C11.1872 14.1161 8.81282 14.1161 7.34835 12.6517M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM8.125 8.125C8.125 8.47018 7.98509 8.75 7.8125 8.75C7.63991 8.75 7.5 8.47018 7.5 8.125C7.5 7.77982 7.63991 7.5 7.8125 7.5C7.98509 7.5 8.125 7.77982 8.125 8.125ZM7.8125 8.125H7.81875V8.1375H7.8125V8.125ZM12.5 8.125C12.5 8.47018 12.3601 8.75 12.1875 8.75C12.0149 8.75 11.875 8.47018 11.875 8.125C11.875 7.77982 12.0149 7.5 12.1875 7.5C12.3601 7.5 12.5 7.77982 12.5 8.125ZM12.1875 8.125H12.1938V8.1375H12.1875V8.125Z"
                            stroke="#3F3F46"
                            strokeWidth="1.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      {showEmojiPicker && (
                        <div
                          ref={emojiPickerRef}
                          className="fixed z-[99999]"
                          style={{
                            bottom: emojiButtonRef.current
                              ? `calc(100vh - ${
                                  emojiButtonRef.current.getBoundingClientRect().top
                                }px + 8px)`
                              : '200px',
                            left: emojiButtonRef.current
                              ? emojiButtonRef.current.getBoundingClientRect().left
                              : 0,
                          }}
                        >
                          <EmojiPicker onEmojiClick={(emojiData) => handleEmojiClick(emojiData)} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mention - hide when formatting menu is open, only show if mentionProvider is provided */}
                  {!showFormattingMenu && mentionProvider && (
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().insertContent('@').run()}
                      className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                      title="Mention User"
                    >
                      <MentionIcon />
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Standard inline formatting toolbar */}
                  {/* Attach - only show if onAttachClick is provided */}
                  {onAttachClick && (
                    <>
                      <button
                        type="button"
                        onClick={onAttachClick ?? triggerFilePicker}
                        className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                        title="Attach file"
                      >
                        <FileUploadIcon />
                      </button>
                      {/* Hidden file input for attachments */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </>
                  )}

                  {/* Emoji */}
                  <div className="relative">
                    <button
                      ref={emojiButtonRef}
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                      title="Insert Emoji"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6517 12.6517C11.1872 14.1161 8.81282 14.1161 7.34835 12.6517M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10ZM8.125 8.125C8.125 8.47018 7.98509 8.75 7.8125 8.75C7.63991 8.75 7.5 8.47018 7.5 8.125C7.5 7.77982 7.63991 7.5 7.8125 7.5C7.98509 7.5 8.125 7.77982 8.125 8.125ZM7.8125 8.125H7.81875V8.1375H7.8125V8.125ZM12.5 8.125C12.5 8.47018 12.3601 8.75 12.1875 8.75C12.0149 8.75 11.875 8.47018 11.875 8.125C11.875 7.77982 12.0149 7.5 12.1875 7.5C12.3601 7.5 12.5 7.77982 12.5 8.125ZM12.1875 8.125H12.1938V8.1375H12.1875V8.125Z"
                          stroke="#3F3F46"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {showEmojiPicker && (
                      <div
                        ref={emojiPickerRef}
                        className="fixed z-[99999]"
                        style={{
                          bottom: emojiButtonRef.current
                            ? `calc(100vh - ${
                                emojiButtonRef.current.getBoundingClientRect().top
                              }px + 8px)`
                            : '200px',
                          left: emojiButtonRef.current
                            ? emojiButtonRef.current.getBoundingClientRect().left
                            : 0,
                        }}
                      >
                        <EmojiPicker onEmojiClick={(emojiData) => handleEmojiClick(emojiData)} />
                      </div>
                    )}
                  </div>

                  {/* Mention - only show if mentionProvider is provided */}
                  {mentionProvider && (
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().insertContent('@').run()}
                      className="p-2 rounded hover:bg-app-grey-20 transition-colors"
                      title="Mention User"
                    >
                      <MentionIcon />
                    </button>
                  )}

                  {/* Link */}
                  <button
                    type="button"
                    onClick={handleLinkClick}
                    className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                      editor.isActive('link') ? 'bg-app-grey-20' : ''
                    }`}
                    title={editor.isActive('link') ? 'Edit Link' : 'Insert Link (Ctrl+L)'}
                  >
                    <HyperlinkIcon />
                  </button>

                  {/* Divider */}
                  <div className="w-px h-6 bg-app-grey-25 mx-1" />

                  {/* Bold */}
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                      editor.isActive('bold') ? 'bg-app-grey-20' : ''
                    }`}
                    title="Bold (Ctrl+B)"
                  >
                    <BoldIcon />
                  </button>

                  {/* Italic */}
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                      editor.isActive('italic') ? 'bg-app-grey-20' : ''
                    }`}
                    title="Italic (Ctrl+I)"
                  >
                    <ItalicIcon />
                  </button>

                  {/* Strikethrough */}
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                      editor.isActive('strike') ? 'bg-app-grey-20' : ''
                    }`}
                    title="Strikethrough"
                  >
                    <StrikethroughIcon />
                  </button>
                  <div className="w-px h-6 bg-app-grey-25 mx-1" />
                  {/* Bullet List */}
                  <button
                    type="button"
                    onClick={() => {
                      if (editor.isActive('bulletList')) {
                        // If already in a bullet list, lift it out to a paragraph
                        editor.chain().focus().liftListItem('listItem').run()
                        return
                      }

                      const { state } = editor
                      const { $from } = state.selection

                      // Check if we're right after a hard break
                      const nodeBefore = $from.nodeBefore
                      if (nodeBefore && nodeBefore.type.name === 'hardBreak') {
                        // Replace the hard break with a paragraph split, then toggle bullet
                        const hardBreakPos = $from.pos - nodeBefore.nodeSize
                        editor
                          .chain()
                          .focus()
                          .setTextSelection(hardBreakPos)
                          .deleteNode('hardBreak')
                          .splitBlock()
                          .toggleBulletList()
                          .run()
                        return
                      }

                      // If cursor is in middle of text (has selection or text before cursor), split first
                      const posInParent = $from.parentOffset
                      const parentNode = $from.parent

                      if (
                        !editor.isActive('bulletList') &&
                        posInParent > 0 &&
                        posInParent < parentNode.content.size
                      ) {
                        // Split at cursor position, then apply bullet to second part
                        editor.chain().focus().splitBlock().toggleBulletList().run()
                        return
                      }

                      // Default: toggle bullet list
                      editor.chain().focus().toggleBulletList().run()
                    }}
                    className={`p-2 rounded hover:bg-app-grey-20 transition-colors ${
                      editor.isActive('bulletList') ? 'bg-app-grey-20' : ''
                    }`}
                    title="Bullet List"
                  >
                    <ListBulletIcon />
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {rightActions}
              {/* Only show submit button in message mode */}
              {mode === 'message' && (
                <button
                  type="submit"
                  disabled={
                    (editor.isEmpty && pendingUploads.length === 0 && !hasAttachments) || disabled
                  }
                  className="w-9 h-9 rounded-2xl bg-app-primary-60 text-white flex items-center justify-center hover:bg-app-primary-70 disabled:bg-app-primary-20 disabled:text-app-primary-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Send"
                >
                  <AppSendMessageIcon />
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Link Dialog */}
      <LinkDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onSave={handleLinkSave}
        onRemove={handleLinkRemove}
        initialText={linkDialogData.text}
        initialUrl={linkDialogData.url}
        isEditing={linkDialogData.isEditing}
      />

      {/* Styles */}
      <style>{`
        .rich-text-input-container .ProseMirror {
          outline: none;
          min-height: 56px;
          padding: 8px 12px;
          border: none;
        }
        .rich-text-input-container .ProseMirror p.is-editor-empty:first-child::before {
          color: var(--color-app-grey-60, #7C7C7C);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
          position: relative;
          top: 0;
        }
      `}</style>
    </div>
  )
}
